import cx from "classnames";
import { Listbox, Transition } from "@headlessui/react";
import { Fragment, ReactNode, useMemo } from "react";
import { FaChevronDown, FaUpDown } from "react-icons/fa6";

export type SelectOption<Key extends string> = {
    key: Key;
    value: string;
};

export type SelectInputProps<Key extends string> = {
    value: Key;
    onSelect: (value: Key) => void;
    options: SelectOption<Key>[];
    label?: string;
    className?: string;
};

export default function SelectInput<Key extends string = string>({
    value,
    onSelect,
    options,
    label,
    className,
}: SelectInputProps<Key>) {
    return (
        <select
            className={cx(
                "relative flex flex-row justify-start items-center self-stretch border-none outline-none appearance-none ring-2 ring-primary-30 focus-within:ring-purple-100 focus-within:ring rounded-lg",
                className,
            )}
            value={value}
            onChange={(e) => onSelect(e.currentTarget.value)}>
            {options.map((option) => (
                <option value={option.key} key={option.key}>
                    {option.value}
                </option>
            ))}
        </select>
    );
}

export type StyledSelectOption<T> = T & {
    value: string;
};

export type StyledSelectProps<T> = {
    value: string;
    onSelect: (value: string) => void;
    options: StyledSelectOption<T>[];
    placholder?: string;
    renderLabel?: (selected: T | null) => ReactNode;
    renderOption: (opt: T | null, idx: number) => ReactNode;
};

export function StyledSelect<T>({
    value,
    onSelect,
    className,
    options,
    placeholder,
    renderLabel,
    renderOption,
}: StyledSelectProps<T>) {
    const hydratedSelected = useMemo(() => {
        if (value == null) {
            return null;
        }
        return options.find((opt) => opt.value === value);
    }, [value, options]);

    return (
        <Listbox value={value} onChange={onSelect}>
            {({ open }) => (
                <>
                    <Listbox.Button
                        className={cx(
                            "max-w-full p-2 pr-4 self-stretch mt-2 relative flex flex-row justify-start items-center self-stretch border-none outline-none appearance-none ring-2 ring-primary-30 focus-within:ring-purple-100 focus-within:ring rounded-lg",
                            className,
                        )}>
                        <span className="block truncate">
                            {renderLabel
                                ? renderLabel(hydratedSelected)
                                : renderOption(hydratedSelected, -1)}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <FaChevronDown
                                className="h-3 w-3 text-neutral-70"
                                aria-hidden="true"
                            />
                        </span>
                    </Listbox.Button>

                    <Transition
                        show={open}
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0">
                        <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-max overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {options.map((option, idx) => (
                                <Listbox.Option
                                    key={option.value}
                                    value={option.value}
                                    className={({ active }) =>
                                        cx(
                                            active && "bg-primary-10",
                                            "relative cursor-default select-none py-2 px-2",
                                        )
                                    }>
                                    {renderOption(option, idx)}
                                </Listbox.Option>
                            ))}
                        </Listbox.Options>
                    </Transition>
                </>
            )}
        </Listbox>
    );
}
